import { Readable } from 'stream';
import DxfArrayScanner from './DxfArrayScanner';
import parseHeader from './header';
import parseTables from './tables/parser';
import { parseBlocks } from './blocks';
import parseEntities from './entities';
import parseObjects from './objects';
import isMatched from './shared/isMatched';
import type { ParsedDxf } from './types';
import DxfParserOptions from './DxfParserOptions';

export default class DxfParser extends EventTarget {
  private readonly decoder: TextDecoder;

  constructor(options: DxfParserOptions = new DxfParserOptions()) {
    super();
    this.decoder = new TextDecoder(options.encoding, {
      fatal: options.encodingFailureFatal,
    });
  }

  /* eslint-disable class-methods-use-this */
  parseSync(dxfString: string) {
    const dxfLinesArray = dxfString.split(/\r\n|\r|\n/g);

    const scanner = new DxfArrayScanner(dxfLinesArray);

    if (!scanner.hasNext()) {
      throw Error('Empty file');
    }

    return DxfParser.parseAll(scanner);
  }

  parseStream(stream: Readable) {
    let dxfString = '';
    return new Promise<ParsedDxf>((res, rej) => {
      stream.on('data', (chunk: string) => {
        dxfString += chunk;
      });
      stream.on('end', () => {
        try {
          const dxfLinesArray = dxfString.split(/\r\n|\r|\n/g);
          const scanner = new DxfArrayScanner(dxfLinesArray);
          if (!scanner.hasNext()) {
            throw Error('Empty file');
          }
          res(DxfParser.parseAll(scanner));
        } catch (err) {
          rej(err);
        }
      });
      stream.on('error', (err: Error) => {
        rej(err);
      });
    });
  }

  /* eslint-disable no-undef */
  /** @param {string} url
   *  @param {RequestInit} [init]
   */
  async parseFromUrl(url: string, init?: RequestInit | undefined) {
    const response = await fetch(url, init);

    if (!response.body) return null;

    const reader = response.body.getReader();
    let buffer = '';
    const { done, value } = await reader.read();

    while (true) {
      if (done) {
        buffer += this.decoder.decode(new ArrayBuffer(0), { stream: false });
        break;
      }
      buffer += this.decoder.decode(value, { stream: true });
    }
    return this.parseSync(buffer);
  }

  private static parseAll(scanner: DxfArrayScanner) {
    const dxf: ParsedDxf = {
      // @ts-ignore
      header: {},
      blocks: {},
      entities: [],
      tables: {},
      objects: {
        byName: {},
        byTree: undefined,
      },
    };
    let curr = scanner.next();

    while (!isMatched(curr, 0, 'EOF')) {
      if (isMatched(curr, 0, 'SECTION')) {
        curr = scanner.next();

        if (isMatched(curr, 2, 'HEADER')) {
          curr = scanner.next();
          dxf.header = parseHeader(curr, scanner);
        } else if (isMatched(curr, 2, 'BLOCKS')) {
          curr = scanner.next();
          dxf.blocks = parseBlocks(curr, scanner);
        } else if (isMatched(curr, 2, 'ENTITIES')) {
          curr = scanner.next();
          dxf.entities = parseEntities(curr, scanner);
        } else if (isMatched(curr, 2, 'TABLES')) {
          curr = scanner.next();
          dxf.tables = parseTables(curr, scanner);
        } else if (isMatched(curr, 2, 'OBJECTS')) {
          curr = scanner.next();
          dxf.objects = parseObjects(curr, scanner);
        }
      }
      curr = scanner.next();
    }
    return dxf;
  }
}
